import { Box } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import { PAGE_VISITED } from "constants/trackingProps";
import AssistantCTA from "features/Home/Sidebar/AssistantPreview/AssistantCTA";
import { trackEvent } from "utils/tracking";

import OutreachTable from "./NewOutreachTable";

const CTA_ENABLED_SUBTABS = new Set(["inbox", "brand-tracker"]);

export default function InfluencerContacts() {
  const { currentOrg } = useContext(OrganizationUserContext);
  const location = useLocation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const alertsRef = useRef<HTMLDivElement>(null);

  // `/dashboard/inbox/<subtab>/...` — segment [3] is the subtab. When empty,
  // NewOutreachTable redirects to the inbox default, so treat that as inbox.
  const subtab = location.pathname.split("/")[3] || "inbox";
  const showCTA = CTA_ENABLED_SUBTABS.has(subtab);
  const ctaCopy =
    subtab === "brand-tracker"
      ? {
          title: "Need help following up with brands?",
          body: "Chat with AI to spot past brands worth pitching again.",
        }
      : {
          title: "Need help with your inbox?",
          body: "Chat with AI for personalized suggestions on what to reply to first.",
        };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  // Publish the alerts block's rendered height as a CSS variable so the
  // inbox scroll regions can subtract it from their `calc(100vh - ...)`.
  useLayoutEffect(() => {
    const alertsEl = alertsRef.current;
    const wrapperEl = wrapperRef.current;
    if (!wrapperEl) return;
    const update = () => {
      wrapperEl.style.setProperty(
        "--inbox-alerts-h",
        `${alertsEl?.offsetHeight ?? 0}px`,
      );
    };
    update();
    if (!alertsEl) return;
    const observer = new ResizeObserver(update);
    observer.observe(alertsEl);
    return () => observer.disconnect();
  }, [showCTA]);

  return (
    <Box ref={wrapperRef} sx={{ "--inbox-alerts-h": "0px" }}>
      {showCTA && (
        <Box
          ref={alertsRef}
          sx={{ px: { xs: 1, sm: 2 }, pt: 2, "&:empty": { p: 0 } }}
        >
          <AssistantCTA
            title={ctaCopy.title}
            body={ctaCopy.body}
            openContext={{ promptPage: "inbox", source: "Inbox CTA" }}
          />
        </Box>
      )}
      <Routes>
        <Route path="*" element={<OutreachTable />} />
      </Routes>
    </Box>
  );
}
